import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import OgShell from '../components/OgShell';
import AuthGate from '../components/AuthGate';
import CaseStepFields from '../components/CaseStepFields';
import MediaSection, { type MediaPatch } from '../components/MediaSection';
import { Field, StepBadge, TextInput } from '../components/fields';
import {
  createQiCaseSeries,
  fetchCase,
  fetchLatestDraft,
  fetchMedia,
  fetchSessions,
  getMyPractitionerProfile,
  getQiErrorMessage,
  getSignedMediaUrl,
  listMyQiCaseSeries,
  listVerifiedPractitioners,
  notifyRecipient,
  persistCase,
  sendQiEmail,
  setMediaPermission,
  uploadQiMedia,
} from '../lib/api';
import type { NotifyResult, VerifiedPractitioner } from '../lib/api';
import { formatDate } from '../lib/constants';
import type { QiCase, QiCaseForm, QiCaseSeries, QiMedia } from '../lib/types';

const STEP_TITLES = [
  '基本資料',
  '施炁前狀況',
  '施炁方式',
  '當下反應',
  '施炁後狀況',
  '證據與附件',
  '額外授權',
  '確認送出',
];

const emptyForm: QiCaseForm = {
  session_datetime: '',
  location: '',
  recipient_alias: '',
  recipient_contact: '',
  recipient_email: '',
  is_first_session: true,

  pre_main_issue: '',
  pre_body_parts: [],
  pre_discomfort: null,
  pre_emotion: null,
  pre_function: null,
  pre_history: '',
  pre_treatment: '',
  pre_source_type: '',
  pre_note: '',

  method_start: '',
  method_end: '',
  method_contact: '',
  method_position: '',
  method_audio: '',
  method_observation: '',
  method_stopped: false,
  method_attention: '',

  reactions: [],
  reaction_other: '',
  reaction_start: '',
  reaction_duration: '',
  reaction_practitioner_note: '',
  reaction_recipient_note: '',

  post_discomfort: null,
  post_emotion: null,
  post_function: null,
  post_recipient_change: '',
  post_practitioner_change: '',
  post_new_discomfort: '',
  post_need_help: '',
  post_next_step: '',
  post_24h_followup: false,

  practitioner_user_id: null,
  practitioner_name: '',
  practitioner_verified: true,

  referrer_type: '',
  referrer_user_id: null,
  referrer_name: '',
  referral_code: '',
  referral_source_text: '',

  storage_consent: false,
  followup_consent: false,
  reviewer_access: false,
  internal_anonymous_use: false,
  public_anonymous_use: false,
  media_public_use: false,
};

export default function NewCasePage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const modeParam = params.get('mode');
  const draftIdParam = Number(params.get('draftId')) || null;
  const seriesIdParam = Number(params.get('seriesId')) || null;

  return (
    <OgShell>
      <div className="max-w-2xl mx-auto px-4 md:px-6">
        <AuthGate>
          {(auth) => {
            const isCandidate = auth.role === 'candidate';
            const isOwner = modeParam === 'owner' || (auth.role !== 'practitioner' && !isCandidate);
            return (
              <NewCaseForm
                userId={auth.userId}
                isOwner={isOwner}
                isCandidate={isCandidate}
                draftId={draftIdParam}
                seriesId={seriesIdParam}
              />
            );
          }}
        </AuthGate>
      </div>
    </OgShell>
  );
}

function NewCaseForm({
  userId,
  isOwner,
  isCandidate,
  draftId,
  seriesId,
}: {
  userId: string;
  isOwner: boolean;
  isCandidate: boolean;
  draftId: number | null;
  seriesId: number | null;
}) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<QiCaseForm>(emptyForm);
  const [caseId, setCaseId] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState<QiCase | null>(null);
  const [notifyResult, setNotifyResult] = useState<NotifyResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [practitioners, setPractitioners] = useState<VerifiedPractitioner[]>([]);
  const [caseSeries, setCaseSeries] = useState<QiCaseSeries[]>([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [media, setMedia] = useState<QiMedia[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<number, string>>({});
  const [mediaUploading, setMediaUploading] = useState(false);
  // React state 更新不是同步的；用 ref 立即鎖定唯一草稿，避免自動儲存與附件上傳
  // 同時各建一份草稿，造成附件掛在另一份草稿後「消失」。
  const caseIdRef = useRef<number | null>(null);
  const draftCreationRef = useRef<Promise<QiCase | null> | null>(null);

  const update = useCallback((patch: Partial<QiCaseForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  // Recover latest draft on mount
  useEffect(() => {
    let cancelled = false;
    const requestedDraft = draftId
      ? fetchCase(draftId).then((qCase) =>
          qCase && qCase.status === 'draft'
            ? {
                id: qCase.id,
                form_draft: (qCase as QiCase & { form_draft?: QiCaseForm | null }).form_draft,
              }
            : null,
        )
      : fetchLatestDraft(userId);

    requestedDraft
      .then((draft) => {
        if (cancelled) return;
        if (draft) {
          caseIdRef.current = draft.id;
          setCaseId(draft.id);
          if (draft.form_draft) setForm({ ...emptyForm, ...draft.form_draft });
          setNotice('已載入上次的草稿，可繼續填寫。');
          // 載入草稿後一併載入該草稿的媒體
          fetchMedia(draft.id).then(async (list) => {
            if (cancelled) return;
            setMedia(list);
            const urlMap: Record<number, string> = {};
            await Promise.all(
              list.map(async (item) => {
                const url = await getSignedMediaUrl(item.file_path);
                if (url) urlMap[item.id] = url;
              }),
            );
            if (!cancelled) setMediaUrls(urlMap);
          }).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingDraft(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, draftId]);

  const ensureDraft = useCallback(async (): Promise<QiCase> => {
    if (caseIdRef.current) {
      const existing = await persistCase(form, userId, caseIdRef.current, false, isOwner, isCandidate);
      if (!existing) throw new Error('無法儲存草稿，請稍後再試。');
      return existing;
    }

    if (!draftCreationRef.current) {
      draftCreationRef.current = persistCase(form, userId, null, false, isOwner, isCandidate)
        .then((created) => {
          if (created) {
            caseIdRef.current = created.id;
            setCaseId(created.id);
          }
          return created;
        })
        .finally(() => {
          draftCreationRef.current = null;
        });
    }

    const created = await draftCreationRef.current;
    if (!created) throw new Error('無法建立草稿，請稍後再試。');
    return created;
  }, [form, userId, isOwner, isCandidate]);

  // 案主模式：載入正式施炁者名單供選擇
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    listVerifiedPractitioners(form.location)
      .then((list) => {
        if (!cancelled) setPractitioners(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOwner, form.location]);

  // 載入既有主訴主線。由 GP 待辦清單進入時，自動帶入尚未建立案例的受施者與主訴。
  useEffect(() => {
    let cancelled = false;
    listMyQiCaseSeries()
      .then((rows) => {
        if (cancelled) return;
        const seriesRows = rows as QiCaseSeries[];
        setCaseSeries(seriesRows);
        if (!draftId && seriesId) {
          const selected = seriesRows.find((series) => series.id === seriesId);
          if (selected) {
            setForm((current) => ({
              ...current,
              case_series_id: selected.id,
              recipient_alias: current.recipient_alias || selected.recipient_alias || '',
              pre_main_issue: current.pre_main_issue || selected.chief_complaint || '',
              location: current.location || selected.location_code || '',
              complaint_source: 'carried_forward',
            }));
          }
        }
      })
      .catch(() => setCaseSeries([]));
    return () => {
      cancelled = true;
    };
  }, [draftId, seriesId]);

  // Email 通知選項：填寫受炁者 Email 後預設開啟，清空則關閉。
  useEffect(() => {
    if (form.recipient_email && !sendEmail) setSendEmail(true);
    else if (!form.recipient_email && sendEmail) setSendEmail(false);
  }, [form.recipient_email, sendEmail]);

  const saveDraft = useCallback(
    async (silent = false) => {
      if (!silent) {
        setSaving(true);
        setError(null);
      }
      try {
        const result = await ensureDraft();
        if (result) {
          caseIdRef.current = result.id;
          setCaseId(result.id);
          if (!silent) setNotice('草稿已儲存，可隨時離開，之後會自動載入。');
        }
      } catch (err) {
        if (!silent) setError(getQiErrorMessage(err, '儲存草稿失敗'));
      } finally {
        if (!silent) setSaving(false);
      }
    },
    [ensureDraft],
  );

  // 保存草稿的最新參照，供翻頁／自動保存使用（避免 stale closure）。
  const saveDraftRef = useRef(saveDraft);
  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  // 自動保存：欄位一有變更，800ms 內自動存為草稿；翻頁、重整都不怕流失。
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      saveDraftRef.current(true);
    }, 800);
    return () => clearTimeout(timer);
  }, [form]);

  const handleMediaUpload = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;
      setMediaUploading(true);
      setError(null);
      try {
        // 確保有草稿 case 才能掛接媒體
        const draft = await ensureDraft();
        const targetId = draft.id;
        const sessions = await fetchSessions(targetId);
        const sessionId = sessions.length > 0 ? sessions[0].id : null;
        const uploaded: QiMedia[] = [];
        for (const file of files) {
          const item = await uploadQiMedia(file, targetId, 'post_condition', userId, false, {
            sessionId,
            evidenceStage: 'after',
          });
          if (item) uploaded.push(item);
        }
        // 立即顯示資料庫 insert 回傳的附件，再以查詢結果校準；即使第二次查詢
        // 因網路延遲暫時看不到，也不會顯示「尚無附件」。
        if (uploaded.length > 0) {
          setMedia((prev) => {
            const ids = new Set(prev.map((item) => item.id));
            return [...prev, ...uploaded.filter((item) => !ids.has(item.id))];
          });
          await Promise.all(uploaded.map(async (item) => {
            const url = await getSignedMediaUrl(item.file_path);
            if (url) setMediaUrls((prev) => ({ ...prev, [item.id]: url }));
          }));
        }
        setNotice('附件已上傳，原始檔存於私人空間，尚未同意公開。');
      } catch (err) {
        setError(getQiErrorMessage(err, '上傳失敗'));
      } finally {
        setMediaUploading(false);
      }
    },
    [ensureDraft, userId],
  );

  const handleMediaPatch = useCallback(
    async (mediaId: number, patch: MediaPatch) => {
      try {
        await setMediaPermission(mediaId, patch);
        setMedia((prev) => prev.map((m) => (m.id === mediaId ? { ...m, ...patch } : m)));
      } catch (err) {
        setError(getQiErrorMessage(err, '更新授權失敗'));
      }
    },
    [],
  );

  const validateSubmit = useCallback((): string[] => {
    // 最小必填：施炁日期、受施者稱呼、施炁者（案主模式）。主訴等可留白、暫存後補。
    const missing: string[] = [];
    if (!form.session_datetime) missing.push('施炁日期與時間');
    if (!form.recipient_alias) missing.push('受施者稱呼');
    if (isOwner && !form.practitioner_user_id && !form.practitioner_name) missing.push('施炁者');
    if (isOwner && !form.case_series_id && !form.pre_main_issue.trim()) missing.push('首次掛號主訴');
    return missing;
  }, [form, isOwner]);

  const submit = useCallback(async () => {
    const missing = validateSubmit();
    if (missing.length > 0) {
      setError(`請先完成：${missing.join('、')}`);
      setStep(1);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // 送出時也以同步 ref 為準，避免剛上傳附件後 React state 尚未刷新而
      // 又建立另一份案例。
      let submissionForm = form;
      if (isOwner && !form.case_series_id) {
        const series = await createQiCaseSeries({
          practitionerId: form.practitioner_user_id,
          recipientAlias: form.recipient_alias,
          chiefComplaint: form.pre_main_issue,
          locationCode: (form.location || 'other') as 'taipei' | 'hualien' | 'other',
        });
        submissionForm = { ...form, case_series_id: series.id, complaint_source: 'entered' };
        setForm(submissionForm);
      }
      const result = await persistCase(submissionForm, userId, caseIdRef.current ?? caseId, true, isOwner, isCandidate);
      if (!result) throw new Error('送出失敗');
      setCaseId(result.id);

      // 施炁者模式：通知案主（站內通知 + 可選 Email）
      if (!isOwner) {
        const doSendEmail = sendEmail && !!form.recipient_email;
        const notify = await notifyRecipient(result.id, doSendEmail);

        if (doSendEmail && notify.email_notification_id && result.recipient_token) {
          const basePath = (__BASE_PATH__ as string).split('/').filter(Boolean).join('/');
          const pathPrefix = basePath ? `/${basePath}` : '';
          const secureUrl = `${window.location.origin}${pathPrefix}/og/qi-healing/follow-up/${result.recipient_token}`;

          let practitionerName = form.practitioner_name || '';
          if (!practitionerName) {
            try {
              const profile = await getMyPractitionerProfile();
              practitionerName = profile?.legal_name || '';
            } catch {
              practitionerName = '';
            }
          }

          try {
            const emailRes = await sendQiEmail({
              notification_id: notify.email_notification_id,
              recipient_email: form.recipient_email,
              recipient_alias: form.recipient_alias,
              practitioner_name: practitionerName || undefined,
              secure_url: secureUrl,
            });
            setNotifyResult({ ...notify, email_status: emailRes.email_status, email_reason: emailRes.reason });
          } catch {
            setNotifyResult({ ...notify, email_status: 'failed', email_reason: 'email_send_error' });
          }
        } else {
          setNotifyResult(notify);
        }
      }

      setSubmitted(result);
    } catch (err) {
      setError(getQiErrorMessage(err, '送出失敗'));
    } finally {
      setSaving(false);
    }
  }, [form, userId, caseId, validateSubmit, isOwner, isCandidate, sendEmail]);

  if (submitted) {
    return <SuccessScreen qCase={submitted} isOwner={isOwner} notifyResult={notifyResult} />;
  }

  if (loadingDraft) {
    return (
      <div className="py-20 text-center">
        <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
        <p className="text-sm text-foreground-500 mt-3">載入中……</p>
      </div>
    );
  }

  const title = isOwner ? '建立我的宇炁案例' : '建立施炁紀錄';
  const subtitle = isOwner
    ? '以第一視角紀錄我的施炁歷程，可分步驟填寫，隨時儲存草稿。'
    : '分步驟填寫施炁紀錄，可隨時儲存草稿，手機亦可完整操作。';

  const submitLabel = saving
    ? '送出中…'
    : isOwner
      ? '同意並儲存我的案例'
      : sendEmail
        ? '儲存並通知受炁者'
        : '儲存紀錄，不發送通知';

  return (
    <div className="space-y-6">
      <header className="text-center space-y-2 pt-2">
        <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          {title}
        </h1>
        <p className="text-xs text-foreground-500">{subtitle}</p>
      </header>

      {isCandidate && (
        <div className="bg-accent-100/70 border border-accent-200 rounded-xl p-4 text-left space-y-2">
          <div className="flex items-start gap-2.5">
            <i className="ri-user-add-line text-accent-600 text-lg mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground-800">準施炁者｜實作培育中</p>
              <p className="text-xs text-foreground-600 leading-relaxed">
                你目前是準施炁者身分，建立的案例一律<b>私人保存</b>，不會公開、不會自動成為見證，也不代表你已取得正式授權資格。正式授權仍需 ABAA／管理員人工核定。
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-foreground-50/60 border border-foreground-200/50 rounded-xl p-3 text-xs text-foreground-500 leading-relaxed flex items-start gap-2">
        <i className="ri-shield-check-line text-accent-500 mt-0.5" />
        <p>
          宇炁療育不取代醫療診斷、治療或緊急醫療服務。身體不適者仍應諮詢合格醫療專業人員；不得要求受施者停止原有醫療或藥物。
        </p>
      </div>

      <div className="flex justify-center">
        <StepBadge current={step} total={8} />
      </div>

      <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 md:p-6">
        <h2 className="text-base font-medium text-foreground-800 mb-4">
          {step}. {STEP_TITLES[step - 1]}
        </h2>

        {step === 6 ? (
          <MediaSection
            media={media}
            mediaUrls={mediaUrls}
            uploading={mediaUploading}
            onUpload={handleMediaUpload}
            onPatch={handleMediaPatch}
            isRecipient={false}
          />
        ) : step === 7 ? (
          <CaseStepFields
            step={7}
            form={form}
            onChange={update}
            isOwner={isOwner}
            practitioners={practitioners}
          />
        ) : step === 2 && isOwner ? (
          <div className="space-y-5">
            <div className="rounded-xl border border-accent-200 bg-accent-50 p-4 space-y-3">
              <p className="text-sm font-medium text-foreground-800">這次是否延續上次的同一主訴？</p>
              <select
                className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm"
                value={form.case_series_id || ''}
                onChange={(event) => {
                  const id = Number(event.target.value) || null;
                  const selected = caseSeries.find((item) => item.id === id);
                  update(selected
                    ? { case_series_id: selected.id, pre_main_issue: selected.chief_complaint, complaint_source: 'carried_forward' }
                    : { case_series_id: null, pre_main_issue: '', complaint_source: 'entered' });
                }}
              >
                <option value="">新的主訴／第一次登錄</option>
                {caseSeries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.chief_complaint}（{item.location_code === 'taipei' ? '臺北' : item.location_code === 'hualien' ? '花蓮' : '其他'}）
                  </option>
                ))}
              </select>
              {form.complaint_source === 'carried_forward' && (
                <p className="text-xs text-accent-700">已自動帶入上次主訴，仍可在下方修正本次描述。</p>
              )}
            </div>
            <CaseStepFields step={2} form={form} onChange={update} isOwner={isOwner} practitioners={practitioners} />
          </div>
        ) : step === 8 ? (
          <div className="space-y-4">
            <SummaryView form={form} isOwner={isOwner} isCandidate={isCandidate} practitioners={practitioners} onEdit={setStep} />
            {!isOwner && (
              <NotifyOptions
                form={form}
                sendEmail={sendEmail}
                onToggleEmail={setSendEmail}
                onChangeEmail={(v) => update({ recipient_email: v })}
              />
            )}
          </div>
        ) : (
          <CaseStepFields
            step={step}
            form={form}
            onChange={update}
            isOwner={isOwner}
            practitioners={practitioners}
          />
        )}
      </div>

      {error && <p className="text-sm text-rose-600 bg-rose-50 rounded-md px-4 py-2.5">{error}</p>}
      {notice && <p className="text-sm text-emerald-600 bg-emerald-50 rounded-md px-4 py-2.5">{notice}</p>}

      {/* Navigation */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => {
            saveDraftRef.current(true);
            setStep((s) => Math.max(1, s - 1));
          }}
          disabled={step === 1}
          className="whitespace-nowrap px-4 py-2.5 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          上一步
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => saveDraft()}
            disabled={saving}
            className="whitespace-nowrap px-4 py-2.5 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving ? '儲存中…' : '儲存草稿'}
          </button>
          {step < 8 ? (
            <button
              onClick={() => {
                saveDraftRef.current(true);
                setStep((s) => Math.min(8, s + 1));
              }}
              className="whitespace-nowrap px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer"
            >
              下一步
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={saving}
              className="whitespace-nowrap px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
            >
              {submitLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NotifyOptions({
  form,
  sendEmail,
  onToggleEmail,
  onChangeEmail,
}: {
  form: QiCaseForm;
  sendEmail: boolean;
  onToggleEmail: (v: boolean) => void;
  onChangeEmail: (v: string) => void;
}) {
  const hasEmail = !!form.recipient_email;
  return (
    <div className="bg-background-50 border border-foreground-200/50 rounded-xl p-4 space-y-3 text-left">
      <p className="text-sm font-medium text-foreground-800">通知受炁者</p>

      <Field
        label="受炁者會員 Email"
        hint="填寫受炁者已註冊的 Email，可自動綁定帳號並建立站內通知；未填寫則只能複製安全連結私下傳送。"
      >
        <TextInput
          type="email"
          value={form.recipient_email}
          onChange={onChangeEmail}
          placeholder="受炁者 Email（選填）"
        />
      </Field>

      <label
        className={`flex items-center gap-2 text-sm cursor-pointer ${
          hasEmail ? 'text-foreground-700' : 'text-foreground-400 cursor-not-allowed'
        }`}
      >
        <input
          type="checkbox"
          checked={sendEmail && hasEmail}
          disabled={!hasEmail}
          onChange={(e) => onToggleEmail(e.target.checked)}
          className="w-4 h-4 accent-accent-500"
        />
        同時以 Email 通知受炁者填寫回報
      </label>

      {!hasEmail && (
        <p className="text-xs text-amber-600">尚未填寫受炁者 Email，請先填寫後才能啟用 Email 通知。</p>
      )}

      <p className="text-xs text-foreground-400 leading-relaxed">
        送出後系統會建立站內通知（受炁者已註冊且 Email 有效時）。若勾選 Email，會同時排入 Email 寄送佇列；Email
        服務尚未配置時，會誠實顯示「尚未寄出」，不影響站內通知。
      </p>
    </div>
  );
}

function SummaryView({
  form,
  isOwner,
  isCandidate,
  practitioners,
  onEdit,
}: {
  form: QiCaseForm;
  isOwner: boolean;
  isCandidate: boolean;
  practitioners: VerifiedPractitioner[];
  onEdit: (step: number) => void;
}) {
  const practitionerLabel = isCandidate
    ? '準施炁者（培育中）'
    : form.practitioner_verified
      ? (practitioners.find((p) => p.user_id === form.practitioner_user_id)?.public_display_name || practitioners.find((p) => p.user_id === form.practitioner_user_id)?.legal_name) || '本人（施炁者）'
      : form.practitioner_name || '未填';

  const missing: { label: string; step: number }[] = [];
  if (!form.session_datetime) missing.push({ label: '施炁日期與時間', step: 1 });
  if (!form.recipient_alias) missing.push({ label: '受施者稱呼', step: 1 });
  if (isOwner && !form.practitioner_user_id && !form.practitioner_name) missing.push({ label: '施炁者', step: 1 });

  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <SummaryItem label="施炁時間" value={formatDate(form.session_datetime)} />
        <SummaryItem label={isOwner ? '我的稱呼' : '受施者稱呼'} value={form.recipient_alias || '—'} />
        <SummaryItem label="地點" value={form.location || '—'} />
        <SummaryItem label="首次接受" value={form.is_first_session ? '是' : '否'} />
      </div>

      <SummaryItem label="施炁者" value={practitionerLabel} />
      <SummaryItem label="主要問題" value={form.pre_main_issue || '—'} />
      <SummaryItem label="施炁前不適" value={form.pre_discomfort === null ? '未填' : `${form.pre_discomfort} / 10`} />
      <SummaryItem label="施炁後不適" value={form.post_discomfort === null ? '未填' : `${form.post_discomfort} / 10`} />
      <SummaryItem label="當下反應" value={form.reactions.length ? form.reactions.join('、') : '未填'} />

      {form.referrer_type && (
        <SummaryItem
          label="介紹人"
          value={form.referrer_name || form.referrer_type}
        />
      )}

      {missing.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <p className="text-xs text-amber-700 font-medium">以下必要資料尚未填寫：</p>
          {missing.map((m) => (
            <div key={m.label} className="flex items-center justify-between gap-2">
              <span className="text-xs text-amber-700">{m.label}</span>
              <button
                onClick={() => onEdit(m.step)}
                className="whitespace-nowrap text-xs px-3 py-1 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer"
              >
                前往修改
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-foreground-50/60 rounded-lg p-4 text-xs text-foreground-500 leading-relaxed">
        {isOwner ? (
          <p>
            點擊下方「同意並儲存我的案例」即代表我確認此案例與本人有關、資料屬實，並同意本案例依隱私政策儲存，僅供本人、施炁者及獲授權的審核人員依權限查看。公開、媒體使用與後續聯絡將另行徵求同意。
          </p>
        ) : (
          <p>
            點擊下方「確認資料正確並儲存案例」即代表我確認這是本人實際參與或獲授權記錄的案例、已據實填寫。此動作只保存受限案例，不代表受施者同意公開。系統隨後會產生受施者確認／回報連結。
          </p>
        )}
      </div>
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-foreground-400">{label}</p>
      <p className="text-foreground-700 mt-0.5 leading-snug">{value}</p>
    </div>
  );
}

function SuccessScreen({
  qCase,
  isOwner,
  notifyResult,
}: {
  qCase: QiCase;
  isOwner: boolean;
  notifyResult: NotifyResult | null;
}) {
  const [copied, setCopied] = useState(false);
  const basePath = (__BASE_PATH__ as string).split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  const recipientLink = qCase.recipient_token
    ? `${window.location.origin}${pathPrefix}/og/qi-healing/follow-up/${qCase.recipient_token}`
    : '';

  const copyLink = async () => {
    if (!recipientLink) return;
    try {
      await navigator.clipboard.writeText(recipientLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-6 text-center">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
        <i className="ri-check-double-line text-emerald-500 text-3xl" />
      </div>
      <div>
        <h1 className="text-xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          {isOwner ? '案例已儲存' : '送出成功'}
        </h1>
        <p className="text-sm text-foreground-500 mt-1">
          {isOwner
            ? '你的宇炁案例已建立，可隨時查看與補充追蹤。'
            : '施炁紀錄已正式建立，並自動產生受施者回報連結。'}
        </p>
      </div>

      <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-3 text-left">
        <div>
          <p className="text-xs text-foreground-400">案例編號</p>
          <p className="text-lg font-medium text-foreground-800">{qCase.case_code || `案例 #${qCase.id}`}</p>
        </div>
        <div>
          <p className="text-xs text-foreground-400">施炁時間</p>
          <p className="text-foreground-700">{formatDate(qCase.session_datetime)}</p>
        </div>

        {!isOwner && notifyResult && (
          <div className="space-y-1.5 border-t border-foreground-200/50 pt-3">
            <p className="text-xs text-foreground-400 mb-1">通知狀態</p>
            {notifyResult.recipient_identified === false ? (
              <p className="text-xs text-amber-600 leading-relaxed">
                受施者尚未綁定會員帳號，請複製安全連結私下傳送給案主。
              </p>
            ) : (
              <>
                <p className="text-xs text-emerald-700">站內通知：已建立並送達。</p>
                <p className="text-xs text-foreground-600 leading-relaxed">{emailStatusLabel(notifyResult)}</p>
              </>
            )}
          </div>
        )}

        {!isOwner && recipientLink && (
          <div className="border-t border-foreground-200/50 pt-3">
            <p className="text-xs text-foreground-400 mb-1">受施者回報連結（備用，可私下傳送給受施者）</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={recipientLink}
                className="flex-1 px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-600"
              />
              <button
                onClick={copyLink}
                className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer"
              >
                {copied ? '已複製' : '複製連結'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Link
          to={`/og/qi-healing/case/${qCase.id}`}
          className="whitespace-nowrap px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
        >
          查看案例時間軸
        </Link>
        <Link
          to="/og/qi-healing"
          className="whitespace-nowrap px-6 py-2.5 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer"
        >
          回到宇炁療育
        </Link>
      </div>
    </div>
  );
}

function emailStatusLabel(result: NotifyResult): string {
  switch (result.email_status) {
    case 'sent':
      return 'Email：已寄出。';
    case 'queued':
      return 'Email：已排入寄送佇列。';
    case 'not_requested':
      return 'Email：未寄送（未勾選 Email 通知）。';
    case 'missing_email':
      return 'Email：未寄出（尚未填寫受炁者 Email）。';
    case 'failed':
      return result.email_reason === 'email_service_not_configured'
        ? 'Email 服務尚未配置，尚未寄出；站內通知已建立。'
        : 'Email：寄送失敗。';
    default:
      return 'Email：尚未寄出。';
  }
}
